import { useEffect, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { HomeContentContainer } from '@/components'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button, Input, Label } from '@/components/ui'
import { useTranslations } from '@/hooks/useTranslations'
import { FontSizeSwitch } from '@/components/common/Header/FontSizeSwitch'
import { PdfFontSizeSwitch } from '@/components/common/Header/PdfFontSizeSwitch'
import { useProfileData } from '@/hooks/useProfile'
import { canEditCompanyProfile } from '@/utils/sidebarNavVisibility'
import {
  getExportPortalStatus,
  setExportPortalPin,
} from '@/modules/export-portal/services/exportPortal'

export const Route = createFileRoute('/(dashboard)/admin/settings')({
  component: AdminSettingsPage,
})

function ExportPinSettingsCard() {
  const t = useTranslations('admin.settings')
  const [configured, setConfigured] = useState<boolean | null>(null)
  const [pin, setPin] = useState('')
  const [confirm, setConfirm] = useState('')
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    getExportPortalStatus()
      .then((res) => {
        if (!cancelled) setConfigured(res.configured)
      })
      .catch(() => {
        if (!cancelled) setConfigured(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    setMessage('')
    setError('')
    if (pin.length < 4) {
      setError(t('export_pin_too_short'))
      return
    }
    if (pin !== confirm) {
      setError(t('export_pin_mismatch'))
      return
    }
    setSaving(true)
    try {
      await setExportPortalPin(pin)
      setConfigured(true)
      setPin('')
      setConfirm('')
      setMessage(t('export_pin_success'))
    } catch {
      setError(t('export_pin_error'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card className="border-border shadow-sm">
      <CardHeader>
        <CardTitle className="text-xl">{t('export_pin_title')}</CardTitle>
        <CardDescription className="text-base leading-relaxed">
          {t('export_pin_description')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {configured == null
            ? '…'
            : configured
              ? t('export_pin_configured')
              : t('export_pin_not_configured')}
        </p>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-muted-foreground">{t('export_pin_url_label')}</span>
          <a
            href="/export"
            target="_blank"
            rel="noreferrer"
            className="font-medium text-primary underline-offset-4 hover:underline"
          >
            /export
          </a>
        </div>
        <form onSubmit={handleSave} className="space-y-3 max-w-sm">
          <div className="space-y-2">
            <Label htmlFor="export-pin-new">{t('export_pin_label')}</Label>
            <Input
              id="export-pin-new"
              type="password"
              inputMode="numeric"
              autoComplete="new-password"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 32))}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="export-pin-confirm">{t('export_pin_confirm_label')}</Label>
            <Input
              id="export-pin-confirm"
              type="password"
              inputMode="numeric"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value.replace(/\D/g, '').slice(0, 32))}
            />
          </div>
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
          {message ? <p className="text-sm text-emerald-700">{message}</p> : null}
          <Button type="submit" disabled={saving}>
            {t('export_pin_save')}
          </Button>
        </form>
        <p className="text-sm text-muted-foreground">{t('export_pin_hint')}</p>
      </CardContent>
    </Card>
  )
}

function AdminSettingsPage() {
  const t = useTranslations('admin.settings')
  const { checkPermission } = useProfileData()
  const canAccess = canEditCompanyProfile(checkPermission)
  const [pdfPreviewScale, setPdfPreviewScale] = useState(1.2)

  if (!canAccess) {
    return (
      <HomeContentContainer title={t('title')}>
        <p className="text-muted-foreground">{t('no_access')}</p>
      </HomeContentContainer>
    )
  }

  return (
    <HomeContentContainer title={t('title')} contentClassName="max-w-4xl">
      <div className="space-y-4">
        <Card className="border-border shadow-sm">
          <CardHeader>
            <CardTitle className="text-xl">{t('font_size_title')}</CardTitle>
            <CardDescription className="text-base leading-relaxed">
              {t('font_size_description')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="inline-flex rounded-2xl border border-border/80 bg-background p-2">
              <FontSizeSwitch />
            </div>
            <p className="text-sm text-muted-foreground">{t('font_size_hint')}</p>
          </CardContent>
        </Card>

        <Card className="border-border shadow-sm">
          <CardHeader>
            <CardTitle className="text-xl">{t('pdf_font_size_title')}</CardTitle>
            <CardDescription className="text-base leading-relaxed">
              {t('pdf_font_size_description')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="inline-flex rounded-2xl border border-border/80 bg-background p-2">
              <PdfFontSizeSwitch onChange={setPdfPreviewScale} />
            </div>
            <p className="text-sm text-muted-foreground">{t('pdf_font_size_hint')}</p>

            <div
              className="rounded-xl border border-border/80 bg-muted/10 p-4"
              style={{ fontSize: `${pdfPreviewScale}em` }}
            >
              <p className="font-semibold">{t('pdf_preview_title')}</p>
              <p className="mt-2 text-muted-foreground">{t('pdf_preview_line_1')}</p>
              <p className="text-muted-foreground">{t('pdf_preview_line_2')}</p>
              <p className="mt-3 font-medium">{t('pdf_preview_total')}</p>
            </div>
          </CardContent>
        </Card>

        <ExportPinSettingsCard />
      </div>
    </HomeContentContainer>
  )
}
